/**
 * Backend `edge_type` vocabulary -> stylesheet keys (R23).
 *
 * Algorithms declare WHAT an edge means with a value from
 * docs/ALGORITHM_FRONTEND_CONTRACT.md section 5; the stylesheet decides how it
 * looks. This table is the one place the two meet. Before it, the SSE flush
 * copied the raw backend string straight into `edgeType`, so a new backend value
 * silently styled whatever selector happened to share its spelling.
 *
 * Unknown values fall back to the default money_flow style and are counted; the
 * first sighting of each unknown value logs one console warning.
 */

export interface EdgeStyleKey {
    /** Value for `data.edgeType` (what the stylesheet selectors match). */
    edgeType: string;
    /** Extra data properties the stylesheet reads for this meaning. */
    data?: Record<string, unknown>;
}

const MONEY_FLOW: EdgeStyleKey = { edgeType: 'transaction' };

export const EDGE_TYPE_STYLE_KEYS: Readonly<Record<string, EdgeStyleKey>> = {
    transaction: MONEY_FLOW,
    counterparty: MONEY_FLOW,
    exposure: MONEY_FLOW,
    trace: MONEY_FLOW,
    cross_link: { edgeType: 'gold-path' },
    subpoena: { edgeType: 'transaction', data: { forensic_edge: 'proof' } },
    proof_anchor: { edgeType: 'transaction', data: { forensic_edge: 'proof' } },
    dex_swap: { edgeType: 'dex-swap' },
    p2p: { edgeType: 'inferred' },
    upi: { edgeType: 'inferred' },
    osint: { edgeType: 'osint-link' },
    counterfeit: { edgeType: 'counterfeit' },
    ledger: { edgeType: 'ledger' },
};

const unknownCounts = new Map<string, number>();

/** Map a backend `edge_type` to its stylesheet key; unknown or empty -> default flow style. */
export function resolveEdgeStyleKey(edgeType: unknown): EdgeStyleKey {
    const raw = typeof edgeType === 'string' ? edgeType.trim() : '';
    if (!raw) return MONEY_FLOW;
    const hit = EDGE_TYPE_STYLE_KEYS[raw];
    if (hit) return hit;
    const n = (unknownCounts.get(raw) ?? 0) + 1;
    unknownCounts.set(raw, n);
    if (n === 1) {
        console.warn(
            `[edge_type] unknown value "${raw}": drawn with the default flow style (see ALGORITHM_FRONTEND_CONTRACT.md section 5)`,
        );
    }
    return MONEY_FLOW;
}

export function getUnknownEdgeTypeCounts(): Record<string, number> {
    return Object.fromEntries(unknownCounts);
}

export function resetUnknownEdgeTypeCounts(): void {
    unknownCounts.clear();
}
